import { Button, Flex, Pagination, Table, Tag } from "antd";
import React, { useState } from "react";
import { clubHasFinance } from "~/featureMatrix.jsx";
import { $t, i18n } from "~/i18n.js";
import { api } from "~/lib/api.js";
import { _parseDateTime } from "~/lib/localize.js";
import { LedgerColumns } from "./LedgerColumns.jsx";

export function LedgerTable({
  ledgerEntries,
  loading,
  setOpenLedger,
  hasUsers,

  pagination,
  currentPageNumber,
  setCurrentPage,
  getLedgers,
  ledgerImportStatus,
  allowClearSelection = false,
  onUpdateLedger = null,
}) {
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [loadingClearAmounts, setLoadingClearAmounts] = useState(false);

  const onSelectChange = (newSelectedRowKeys) => {
    setSelectedRowKeys(newSelectedRowKeys);
  };

  const rowSelection = allowClearSelection ? {
    selectedRowKeys,
    onChange: onSelectChange,
    getCheckboxProps: (record) => ({
      disabled: !!record.payment_status, // Disable checkbox if payment_status exists
    }),
  } : null;

  const clearAmounts = async () => {
    setLoadingClearAmounts(true);
    
    for (const ledgerId of selectedRowKeys) {
      const ledger = ledgerEntries.find(l => l.id === ledgerId);
      if (ledger && !ledger.payment_status) {
        const payload = { amount: 0, id: ledger.id };
        
        try {
          await api._call_method("PUT", `financial/ledgers/ledger.json`, payload);
          
          // Update the ledger in the UI if callback provided
          if (onUpdateLedger) {
            onUpdateLedger({ ...ledger, amount: 0 });
          }
        } catch (error) {
          console.error(`Failed to clear amount for ledger ${ledger.id}:`, error);
        }
      }
    }
    
    setSelectedRowKeys([]);
    setLoadingClearAmounts(false);
  };

  const columns = LedgerColumns({ 
    onEdit: clubHasFinance() ? setOpenLedger : null, 
    hasUsers: hasUsers,
    ledgerImportStatus: ledgerImportStatus,
  });
  return (
    <section>
      <Table
        rowSelection={rowSelection}
        columns={columns}
        dataSource={ledgerEntries}
        loading={loading}
        rowKey="id"
        size="small"
        pagination={false}
        style={{ marginBottom: 24 }}
        expandable={{
          expandedRowRender: (record) => {
            const hasLines = record.lines && record.lines.length > 0 ;
            const hasRemark = record.remark?.length > 0;
            const hasCorrection = !record.is_custom && !record.payment_status && record.amount_calculated !== record.amount;
            const hasError = record.error?.length > 0;
            return (
              <div style={{ background: "#f6f6f6", padding: 6, marginLeft: 24 }}>
              {hasError && (
                <div style={{ marginBottom: 8 }}>
                  <Tag color="red">{record.error}</Tag>
                </div>
              )}
              {hasRemark && <p style={{fontStyle: 'italic', marginBottom: 2}}>{record.remark}</p>}
              {hasLines && (
                <ul style={{ marginBottom: hasCorrection ? 12 : 0, listStyle: "circle", paddingLeft: 24 }}>
                {record.lines?.map((line, i) => (
                  <li key={i} style={{ listStyleType: "circle" }}>
                  <span className="label" style={{marginRight: 6}}>{line.description}</span>
                  <span>{i18n.parseLocaleCurrencyString(line.price)}</span>
                  </li>
                ))}
                </ul>
              )}
              {record.is_custom && record.admin_updated_by && (
                <div>
                {$t(
                  "Laatst gewijzigd door ${admin_updated_by} op ${admin_updated_at}",
                  {
                  admin_updated_by: record.admin_updated_by?.name || "-",
                  admin_updated_at:
                    _parseDateTime(record.admin_updated_at) || "-",
                  }
                )}
                </div>
              )}
              {hasCorrection && (
                <div>
                {$t(
                  "Bedrag aangepast door ${admin_updated_by} op ${admin_updated_at}",
                  {
                  admin_updated_by: record.admin_updated_by?.name || "-",
                  admin_updated_at:
                    _parseDateTime(record.admin_updated_at) || "-",
                  }
                )}
                </div>
              )}
              </div>
            );
          },
          rowExpandable: (record) =>
            record.error?.length > 0 ||
            record.remark?.length > 0 ||
            (record.amount_calculated !== record.amount && !record.payment_status) ||
            record.is_custom || (record.lines && record.lines.length > 0),
        }}
      />

      {allowClearSelection && selectedRowKeys.length > 0 && (
        <Flex align="center" gap="middle" style={{ marginBottom: 16 }}>
          <p>
            {$t("${n} geselecteerde mutatie(s)", { n: selectedRowKeys.length })}
          </p>
          
          <Button 
            size="small" 
            loading={loadingClearAmounts}
            onClick={clearAmounts}
            danger
          >
            {$t("Bedrag op 0 zetten")}
          </Button>
        </Flex>
      )}

      <Pagination
        simple={true}
        onChange={(newPage) => {
          setCurrentPage(newPage);
          getLedgers(newPage);
        }}
        showSizeChanger={false}
        pageSize={100}
        current={currentPageNumber}
        hideOnSinglePage={true}
        total={pagination?.count || ledgerEntries.length}
      />
    </section>
  );
}
